// Phase H · Super-admin context · state globale per override cross-tenant.
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

import {
    adminBlogM14PhaseHApi,
    readOverrideState,
    writeOverrideState,
} from "@/lib/adminBlogM14PhaseHApi";

const PhaseHSuperAdminContext = createContext(null);

export function PhaseHSuperAdminProvider({ children }) {
    const [me, setMe] = useState(null);                // { super_admin, tenant_id, ... }
    const [loading, setLoading] = useState(true);
    const [tenants, setTenants] = useState([]);
    const [override, setOverride] = useState(() => readOverrideState());

    const refreshMe = useCallback(async () => {
        try {
            const data = await adminBlogM14PhaseHApi.me();
            setMe(data);
            if (data?.super_admin) {
                try {
                    const r = await adminBlogM14PhaseHApi.tenantsAvailable();
                    setTenants(Array.isArray(r?.tenants) ? r.tenants : []);
                } catch (_e) {
                    setTenants([]);
                }
            }
        } catch (_e) {
            setMe(null);
        }
        setLoading(false);
    }, []);

    useEffect(() => { refreshMe(); }, [refreshMe]);

    // Persistenza selezione cross-tab (storage event).
    useEffect(() => {
        const handler = (e) => {
            if (e.key && e.key.includes("phase_h_super_admin_override")) {
                setOverride(readOverrideState());
            }
        };
        window.addEventListener("storage", handler);
        return () => window.removeEventListener("storage", handler);
    }, []);

    const setMode = useCallback(async (mode, targetTenantId) => {
        const prev = readOverrideState();
        const next = {
            mode,
            target_tenant_id: mode === "specific" ? (targetTenantId || null) : null,
        };
        writeOverrideState(next);
        setOverride(next);
        // Audit event (best-effort, no block UI).
        try {
            await adminBlogM14PhaseHApi.auditTenantOverride({
                mode,
                target_tenant_id: next.target_tenant_id,
                previous_mode: prev.mode,
                previous_tenant_id: prev.target_tenant_id || undefined,
            });
        } catch (_e) { /* silent */ }
    }, []);

    const value = useMemo(() => ({
        me, loading, tenants, override, setMode, refreshMe,
    }), [me, loading, tenants, override, setMode, refreshMe]);

    return (
        <PhaseHSuperAdminContext.Provider value={value}>
            {children}
        </PhaseHSuperAdminContext.Provider>
    );
}

export function usePhaseHSuperAdmin() {
    const ctx = useContext(PhaseHSuperAdminContext);
    if (!ctx) {
        throw new Error("usePhaseHSuperAdmin must be used within PhaseHSuperAdminProvider");
    }
    return ctx;
}
